import {useEffect, useState} from 'react'
import {markLocationWorking} from '@/utils/locationSignal'

/**
 * WHERE THE PASSENGER IS — their own device, from the browser.
 *
 * The tracking map is centred here rather than on the car. A passenger is the STILL thing
 * in this picture: they are standing at a kerb watching a vehicle come to them, and a
 * camera that rides with the car swings the whole city around while the one point they
 * care about slides off screen.
 *
 * ── ⚠️ IT ASKS, AND IT COPES WITH BEING REFUSED ───────────────────────────────────
 * Geolocation is a permission prompt. A passenger can decline it, be on a browser that
 * has it disabled, or be indoors with no fix — so `position` staying null is an ordinary
 * outcome, not an error path. The map falls back to following the driver in that case,
 * which is worse but still useful, and nothing on the page is blocked on the answer.
 *
 * ── ⚠️ THIS HOOK STILL SENDS NOTHING ANYWHERE ────────────────────────────────
 * The position IS now broadcast to the driver — `POST /trips/{trip}/location`, which puts
 * their pin on the driver's map — but by `useShareMyLocation`, not by this. Reading your
 * own location to centre your own map is a different act from handing it to another
 * person, and keeping the two apart means a caller of this hook is never silently a
 * disclosure. `TrackDriver` calls both; nothing else calls the second.
 */

/**
 * ⚠️ HIGH ACCURACY, ON (2026-09-22) — AND THE OLD REASONING WAS SOUND UNTIL THE JOB CHANGED.
 *
 * It used to be `{enableHighAccuracy: false, maximumAge: 30000}`, and the argument for that
 * is still worth reading: a phone asked for high accuracy wakes the GPS radio and holds it,
 * and a passenger who only needs the camera pointed at roughly where they are standing
 * should not pay for that with the battery of someone waiting twenty minutes.
 *
 * What changed is who reads the answer. This fix is now POSTed to the driver (see the note
 * above about `useShareMyLocation`), where it is drawn as the passenger's pin on a
 * navigation map — the thing a driver steers toward at a kerb. Two settings that were
 * harmless for centring your own map are not harmless for that:
 *
 *   `enableHighAccuracy: false`  lets the browser answer from wifi or cell instead of GPS.
 *                                Tens of metres to over a hundred, against the 5-20m the
 *                                driver's own app gets — so on ONE phone, with the driver
 *                                app and this open side by side, the car and the pin were
 *                                drawn twenty-odd metres apart. That was reported as a bug
 *                                and it was this.
 *
 *   `maximumAge: 30000`          accepts a CACHED fix up to thirty seconds old. A pin can
 *                                therefore show where the passenger was half a minute ago,
 *                                which at walking pace is the wrong end of the block and at
 *                                a kerbside pickup is the wrong kerb.
 *
 * ⚠️ THE BATTERY COST IS BOUNDED BY WHO TURNS THIS ON, which is what makes it affordable.
 * `AppLayout` enables the sharing hook only while a trip is EnRoute (and, from an airport,
 * only after the flight has landed) — the window in which a driver is actively coming for
 * this person. It is not a radio held for the whole session.
 *
 * `maximumAge: 0` rather than a few seconds: the sender ticks every 2s, and a fix that is
 * allowed to be older than the interval means posting the same stale coordinate twice.
 */
const OPTIONS = {enableHighAccuracy: true, maximumAge: 0, timeout: 20000}

/**
 * THE MOST RECENT FIX ANY WATCH IN THIS TAB HAS SEEN.
 *
 * ⚠️ THE PORTAL RUNS MORE THAN ONE WATCH, and they do not know about each other.
 * `AppLayout` holds one open for the whole signed-in session while a driver is on the way; a
 * screen that needs the position for itself calls this hook again and gets a SECOND watch.
 * With `maximumAge: 0` that second one refuses the answer the first is already receiving
 * every couple of seconds and waits for a cold read of the GPS radio instead — ten seconds
 * or so indoors, with the app holding the answer the whole time.
 *
 * So every fix is published here on its way past, and a screen that would otherwise open
 * empty can seed itself from it. `getLatestFix` is a READ of what the app already knows: it
 * starts nothing, asks for no permission, and is null until some watch has reported.
 *
 * ⚠️ IT IS A SEED, NOT A SUBSTITUTE. The reader still runs its own watch and takes the
 * live value the moment it lands. Nothing here decides how old is too old, because that
 * depends on the question being asked — opening a map is not the same as deciding whether
 * someone has reached the pickup booth.
 *
 * ⚠️ AND IT DIES WITH THE TAB. A position that outlived a reload would put someone where
 * they were yesterday.
 */
let latestFix = null

export function getLatestFix() {
    return latestFix
}

export function useMyLocation({enabled = true} = {}) {
    const [position, setPosition] = useState(null)
    const [denied, setDenied] = useState(false)

    useEffect(() => {
        if (!enabled || typeof navigator === 'undefined' || !navigator.geolocation) return undefined

        const onFix = (fix) => {
            markLocationWorking() // see utils/locationSignal (iOS)
            const {latitude, longitude, accuracy, heading, speed} = fix?.coords || {}
            if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return
            /* ⚠️ PUBLISHED BEFORE THE STATE UPDATE, NOT INSIDE IT. The updater below is run
               by React and may be run more than once; writing a module variable from it is a
               side effect in a function that has to stay pure. */
            latestFix = {
                lat: latitude,
                lng: longitude,
                accuracy_m: Number.isFinite(accuracy) ? accuracy : null,
                heading_deg: Number.isFinite(heading) ? heading : null,
                speed_mps: Number.isFinite(speed) ? speed : null,
            }
            setPosition((prev) => {
                /* ⚠️ IDENTITY IS LOAD-BEARING. This object is a camera trigger in
                   `RouteMap`, so republishing a new one for a fix that has not moved would
                   re-frame the map every few seconds while the passenger stands still.
                   Six decimal places is about 0.1m — below any real movement and above the
                   jitter a stationary device produces. */
                if (prev && prev.lat.toFixed(6) === latitude.toFixed(6) && prev.lng.toFixed(6) === longitude.toFixed(6)) {
                    return prev
                }
                /* ⚠️ THE WHOLE FIX, NOT JUST THE POINT. The camera only needs lat and lng,
                   but the driver's map is told how much to trust the pin from `accuracy_m`
                   and draws a facing from the heading — and those are only available here,
                   on the frame the browser handed over. Reconstructing them later is not
                   possible. Speed stays in metres per second, the unit it arrives in; the
                   conversion to km/h belongs at the boundary that requires it. */
                return latestFix
            })
        }

        const onError = (err) => {
            // 1 is PERMISSION_DENIED. A timeout or an unavailable fix is not a refusal —
            // it may well succeed on the next attempt — so only the refusal is recorded.
            if (err?.code === 1) setDenied(true)
        }

        /* A WATCH, not a single read. A passenger walking out of a terminal to the kerb
           moves a few hundred metres, and the camera should go with them. */
        const id = navigator.geolocation.watchPosition(onFix, onError, OPTIONS)
        return () => navigator.geolocation.clearWatch(id)
    }, [enabled])

    return {position, denied, hasPosition: !!position}
}

export default useMyLocation
